/**
 * CrossBranchPaymentPage — follows PaymentsPage UI layout.
 * Two-panel: left = customer list (filtered by target branch AR), right = payment form.
 * Extra: Target branch selector above customer list.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import CalcInput from '../components/CalcInput';
import UploadQRDialog from '../components/UploadQRDialog';
import PrintEngine from '../lib/PrintEngine';
import { toast } from 'sonner';
import {
  Search, Users, Building2, Receipt, Banknote, CreditCard, Smartphone,
  Zap, X, ArrowDownAZ, ArrowDown01, Lock, Camera, History, DollarSign,
  FileText, ChevronDown, Loader2, RefreshCw, Clock, TrendingUp, TrendingDown,
  ChevronRight, Wallet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const METHODS = [
  { value: 'Cash', label: 'Cash', icon: Banknote },
  { value: 'Check', label: 'Check', icon: Receipt },
  { value: 'Bank Transfer', label: 'Bank', icon: Building2 },
  { value: 'GCash', label: 'GCash', icon: Smartphone },
  { value: 'Maya', label: 'Maya', icon: CreditCard },
];

const TYPE_CONFIG = {
  penalty_charge: { label: 'Penalty', cls: 'bg-red-100 text-red-700 border-red-200' },
  interest_charge: { label: 'Interest', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  farm_expense: { label: 'Farm', cls: 'bg-green-100 text-green-700 border-green-200' },
  cash_advance: { label: 'Cash Out', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
};
const getTypeConfig = (t) => TYPE_CONFIG[t] || { label: 'Invoice', cls: 'bg-slate-100 text-slate-700 border-slate-200' };

function round2(n) { return Math.round(n * 100) / 100; }

const STATUS_COLORS = {
  pending_review: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  otp_pending: 'bg-emerald-100 text-emerald-800',
  finalized: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-700',
};

export default function CrossBranchPaymentPage() {
  const { user, effectiveBranchId, branches } = useAuth();

  // ── Page tab ──
  const [pageTab, setPageTab] = useState('payment'); // 'payment' | 'history' | 'ledger'

  // ── Inter-branch ledger ──
  const [ledger, setLedger] = useState(null);

  // ── Bulk settlement (BT Batch 2 P2) ──
  const [settleDialog, setSettleDialog] = useState(null); // { group } from owe_to_others
  const [settleMode, setSettleMode] = useState('internal');
  const [settleBankRef, setSettleBankRef] = useState('');
  const [settleNotes, setSettleNotes] = useState('');
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleResult, setSettleResult] = useState(null); // settlement doc after success
  const [settlePosition, setSettlePosition] = useState(null);
  const [settlePhotoQR, setSettlePhotoQR] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [expandedOweGroups, setExpandedOweGroups] = useState(new Set());
  const [expandedOwedGroups, setExpandedOwedGroups] = useState(new Set());

  // ── Target branch ──
  const otherBranches = (branches || []).filter(b => b.id !== effectiveBranchId);
  const [creditBranchId, setCreditBranchId] = useState('');
  const creditBranchName = otherBranches.find(b => b.id === creditBranchId)?.name || '';

  // ── Left panel: customer list (AR at credit branch) ──
  const [custList, setCustList] = useState([]);
  const [listSearch, setListSearch] = useState('');
  const [sortBy, setSortBy] = useState('balance');
  const [loadingCust, setLoadingCust] = useState(false);

  // ── Selected customer ──
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // ── Invoices ──
  const [invoices, setInvoices] = useState([]);
  const [rowAmounts, setRowAmounts] = useState({});
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // ── Payment header ──
  const [payMethod, setPayMethod] = useState('Cash');
  const [payRef, setPayRef] = useState('');
  const [paymentAmtInput, setPaymentAmtInput] = useState('');
  const [pin, setPin] = useState('');
  const [processing, setProcessing] = useState(false);

  // ── Photos ──
  const [flowId] = useState(() => `cbp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [showNotebookUpload, setShowNotebookUpload] = useState(false);
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [notebookPhotoCount, setNotebookPhotoCount] = useState(0);
  const [receiptPhotoCount, setReceiptPhotoCount] = useState(0);

  // ── History ──
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Load customers with AR at credit branch ──
  const loadCustList = useCallback(async () => {
    if (!creditBranchId) { setCustList([]); return; }
    setLoadingCust(true);
    try {
      const res = await api.get(`${API}/api/cross-branch-payments/customers-with-ar/${creditBranchId}`);
      setCustList(res.data || []);
    } catch { setCustList([]); }
    setLoadingCust(false);
  }, [creditBranchId]);

  useEffect(() => { loadCustList(); }, [loadCustList]);

  // ── Load invoices for selected customer at credit branch ──
  const loadInvoices = useCallback(async (custId) => {
    if (!creditBranchId || !custId) return;
    setLoadingInvoices(true);
    try {
      const res = await api.get(`${API}/api/cross-branch-payments/customer-ar/${creditBranchId}/${custId}`);
      setInvoices(res.data.invoices || []);
      setRowAmounts({});
    } catch { setInvoices([]); }
    setLoadingInvoices(false);
  }, [creditBranchId]);

  // ── Load history ──
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`${API}/api/cross-branch-payments/?limit=50`);
      setHistory(res.data.payments || []);
    } catch { /* silent */ }
    setLoadingHistory(false);
  }, []);

  useEffect(() => { if (pageTab === 'history') loadHistory(); }, [pageTab, loadHistory]);

  // ── Load inter-branch ledger ──
  const loadLedger = useCallback(async () => {
    setLoadingLedger(true);
    try {
      const res = await api.get(`${API}/api/cross-branch-payments/inter-branch-ledger`);
      setLedger(res.data);
    } catch { toast.error('Failed to load inter-branch ledger'); }
    setLoadingLedger(false);
  }, []);

  useEffect(() => { if (pageTab === 'ledger') loadLedger(); }, [pageTab, loadLedger]);

  // ── Bulk settlement handlers (BT Batch 2 P2) ──
  const canSettle = user?.role === 'admin' || user?.permissions?.cross_branch_payments?.settle;

  const openSettleDialog = async (group) => {
    setSettleDialog({ group });
    setSettleMode('internal');
    setSettleBankRef('');
    setSettleNotes('');
    setSettleResult(null);
    setSettlePosition(null);
    try {
      const res = await api.get(`${API}/api/cross-branch-payments/branch-position/${ledger.acting_branch_id}`);
      setSettlePosition(res.data);
    } catch { /* position card is optional */ }
  };

  const submitSettle = async () => {
    if (!settleDialog) return;
    if (settleMode === 'documented' && !settleBankRef.trim()) {
      toast.error('Documented settlement needs the bank transaction reference');
      return;
    }
    setSettleBusy(true);
    try {
      const res = await api.post(`${API}/api/cross-branch-payments/settle-bulk`, {
        collecting_branch_id: ledger.acting_branch_id,
        credit_branch_id: settleDialog.group.branch_id,
        mode: settleMode,
        bank_reference: settleBankRef.trim(),
        notes: settleNotes.trim(),
      });
      setSettleResult(res.data.settlement);
      toast.success(res.data.message);
      loadLedger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Settlement failed');
    }
    setSettleBusy(false);
  };

  const printSettlement = () => {
    if (!settleResult) return;
    PrintEngine.print({ type: 'settlement', data: settleResult, format: 'dot_matrix' });
  };

  const toggleOweGroup = (name) => setExpandedOweGroups(prev => {
    const s = new Set(prev); s.has(name) ? s.delete(name) : s.add(name); return s;
  });
  const toggleOwedGroup = (name) => setExpandedOwedGroups(prev => {
    const s = new Set(prev); s.has(name) ? s.delete(name) : s.add(name); return s;
  });

  // ── Customer selection ──
  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setRowAmounts({});
    setPayRef('');
    setPaymentAmtInput('');
    setPin('');
    loadInvoices(c.id);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setInvoices([]);
    setRowAmounts({});
    setPaymentAmtInput('');
    setPin('');
  };

  // ── Filter + sort customers ──
  const filteredList = custList
    .filter(c => {
      if (!listSearch) return true;
      return c.name?.toLowerCase().includes(listSearch.toLowerCase());
    })
    .sort((a, b) =>
      sortBy === 'balance' ? (b.balance - a.balance) : a.name.localeCompare(b.name)
    );

  // ── Calculations ──
  const totalApplied = invoices.reduce((s, inv) => {
    const v = parseFloat(rowAmounts[inv.id] || 0);
    return s + (isNaN(v) ? 0 : v);
  }, 0);
  const totalOpen = invoices.reduce((s, i) => s + (parseFloat(i.balance) || 0), 0);

  // ── Auto-apply ──
  const autoApply = (totalAmt) => {
    setPaymentAmtInput(String(totalAmt ?? ''));
    const amt = parseFloat(totalAmt) || 0;
    if (amt <= 0) { setRowAmounts({}); return; }
    let remaining = amt;
    const newAmounts = {};
    for (const inv of invoices) {
      if (remaining <= 0) break;
      const bal = parseFloat(inv.balance) || 0;
      const apply = Math.min(remaining, bal);
      if (apply > 0) { newAmounts[inv.id] = apply.toFixed(2); remaining = round2(remaining - apply); }
    }
    setRowAmounts(newAmounts);
  };

  // ── Submit payment ──
  const handleSubmit = async () => {
    if (!pin) { toast.error('Manager/Admin PIN required'); return; }
    if (totalApplied <= 0) { toast.error('Enter payment amounts for at least one invoice'); return; }
    if (!effectiveBranchId || effectiveBranchId === 'all') {
      toast.error("Please select a specific source branch from the branch switcher (top-right). 'All Branches' isn't allowed for cross-branch payments.");
      return;
    }
    if (effectiveBranchId === creditBranchId) {
      toast.error('Source and target branch are the same. Switch the source branch first.');
      return;
    }

    setProcessing(true);
    try {
      const allocs = invoices
        .map(inv => ({
          invoice_id: inv.id,
          amount_applied: parseFloat(rowAmounts[inv.id] || 0),
        }))
        .filter(a => a.amount_applied > 0);

      await api.post(`${API}/api/cross-branch-payments/`, {
        collecting_branch_id: effectiveBranchId,
        credit_branch_id: creditBranchId,
        customer_id: selectedCustomer.id,
        invoices: allocs,
        pin,
        payment_method: payMethod,
        reference_number: payRef,
        notebook_photo_upload_id: flowId,
        receipt_photo_upload_id: flowId,
      });

      toast.success('Cross-branch payment submitted for review');
      clearCustomer();
      setPin('');
      await loadCustList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit payment');
    }
    setProcessing(false);
  };

  // ── Account summary ──
  const principalOpen = invoices
    .filter(i => i.sale_type !== 'interest_charge' && i.sale_type !== 'penalty_charge')
    .reduce((s, i) => s + (parseFloat(i.balance) || 0), 0);
  const interestOpen = invoices
    .filter(i => i.sale_type === 'interest_charge' || i.sale_type === 'penalty_charge')
    .reduce((s, i) => s + (parseFloat(i.balance) || 0), 0);

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] animate-fadeIn bg-white" data-testid="cross-branch-payment-page">

      {/* ══════════ PAGE TAB HEADER ══════════ */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-200 bg-white shrink-0">
        <button
          onClick={() => setPageTab('payment')}
          data-testid="tab-cross-branch-payment"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            pageTab === 'payment' ? 'bg-[#1A4D2E] text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}>
          <Building2 size={13} /> Cross-Branch Payment
        </button>
        <button
          onClick={() => setPageTab('history')}
          data-testid="tab-payment-history"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            pageTab === 'history' ? 'bg-[#1A4D2E] text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}>
          <History size={13} /> Payment History
        </button>
        <button
          onClick={() => setPageTab('ledger')}
          data-testid="tab-inter-branch-ledger"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            pageTab === 'ledger' ? 'bg-[#1A4D2E] text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}>
          <Wallet size={13} /> Inter-Branch Account
        </button>
      </div>

      {/* ══════════ HISTORY TAB ══════════ */}
      {pageTab === 'history' && (
        <div className="flex-1 overflow-auto">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold">Cross-Branch Payment History</h2>
            <Button variant="outline" size="sm" className="h-8 gap-1" onClick={loadHistory} data-testid="hist-refresh-btn">
              <RefreshCw size={13} /> Refresh
            </Button>
          </div>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : history.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Receipt size={48} className="mx-auto text-slate-200 mb-3" />
                <p className="text-slate-400 text-sm">No cross-branch payments yet</p>
              </div>
            </div>
          ) : (
            <div className="px-4">
              <table className="w-full text-sm" data-testid="history-table">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Payment #</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Customer</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Collecting Branch</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Credit Branch</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                    <th className="text-center px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(p => (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`history-row-${p.id}`}>
                      <td className="px-3 py-2.5 font-medium text-slate-800">{p.payment_number}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.customer_name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.collecting_branch_name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.credit_branch_name}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{formatPHP(p.grand_total)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge className={`text-[10px] ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>{p.status?.replace('_', ' ')}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-400">{(p.created_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════ PAYMENT TAB ══════════ */}
      {pageTab === 'payment' && (
        <div className="flex flex-1 overflow-hidden">

          {/* ══════════ LEFT: Branch Selector + Customer List ══════════ */}
          <div className="w-72 shrink-0 flex flex-col border-r border-slate-200" data-testid="customer-list-panel">

            {/* Target branch selector */}
            <div className="p-3 border-b border-slate-200 bg-slate-50" data-testid="branch-selector">
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                Credit Branch (AR Owner)
              </Label>
              <div className="relative">
                <Building2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={creditBranchId}
                  onChange={e => {
                    setCreditBranchId(e.target.value);
                    clearCustomer();
                  }}
                  className="w-full h-9 pl-8 pr-7 text-sm border border-slate-200 rounded-md bg-white appearance-none font-medium"
                  data-testid="credit-branch-select"
                >
                  <option value="">Select target branch...</option>
                  {otherBranches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Search + controls */}
            {creditBranchId && (
              <div className="p-3 border-b border-slate-100 space-y-2">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={listSearch} onChange={e => setListSearch(e.target.value)}
                    placeholder="Filter customers..." className="pl-8 h-8 text-sm"
                    data-testid="customer-list-search" />
                </div>
                <div className="flex items-center justify-between gap-1">
                  <Badge variant="outline" className="text-[9px]">
                    With Balance at {creditBranchName}
                  </Badge>
                  {/* Sort toggle */}
                  <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="sort-toggle">
                    <button
                      onClick={() => setSortBy('balance')}
                      title="Sort by balance (highest first)"
                      className={`px-1.5 py-1 transition-colors ${
                        sortBy === 'balance' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                      data-testid="sort-by-balance">
                      <ArrowDown01 size={13} />
                    </button>
                    <button
                      onClick={() => setSortBy('name')}
                      title="Sort by name (A-Z)"
                      className={`px-1.5 py-1 transition-colors ${
                        sortBy === 'name' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                      data-testid="sort-by-name">
                      <ArrowDownAZ size={13} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-400">
                  <span>{filteredList.length} customer{filteredList.length !== 1 ? 's' : ''}</span>
                  <span className="font-semibold text-red-500">
                    {formatPHP(filteredList.reduce((s, c) => s + (c.balance || 0), 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Customer rows */}
            <ScrollArea className="flex-1">
              {!creditBranchId ? (
                <div className="text-center py-12 px-4">
                  <Building2 size={32} className="mx-auto text-slate-200 mb-2" />
                  <p className="text-xs text-slate-400">Select a credit branch above</p>
                </div>
              ) : loadingCust ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : filteredList.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">No customers with balance</p>
              ) : (
                filteredList.map(c => {
                  const isSelected = selectedCustomer?.id === c.id;
                  return (
                    <button key={c.id} onClick={() => selectCustomer(c)}
                      className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                        isSelected ? 'bg-[#1A4D2E]/5 border-l-2 border-l-[#1A4D2E]' : ''
                      }`}
                      data-testid={`customer-row-${c.id}`}>
                      <p
                        className="text-sm font-semibold text-slate-800 leading-tight break-words"
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        title={c.name}
                      >
                        {c.name}
                      </p>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {c.invoice_count} inv{c.invoice_count !== 1 ? 's' : ''}
                          </span>
                          {c.overdue_balance > 0 && (
                            <Badge className="text-[9px] bg-red-100 text-red-700 px-1 py-0 h-4">
                              {formatPHP(c.overdue_balance)} DUE
                            </Badge>
                          )}
                          {c.interest_rate > 0 && (
                            <span className="text-[9px] text-amber-500">{c.interest_rate}%</span>
                          )}
                        </div>
                        <span className={`text-xs font-bold  shrink-0 ${c.balance > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                          {formatPHP(c.balance)}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </ScrollArea>
          </div>

          {/* ══════════ RIGHT: Payment Form ══════════ */}
          <div className="flex-1 flex flex-col overflow-hidden">

            {/* ── Compact Header ── */}
            <div className="border-b border-slate-200 px-5 py-3 shrink-0 bg-white">
              {/* Row 1: Title + Selected customer + Total */}
              <div className="flex items-start gap-4 mb-3">
                <div className="shrink-0 pt-1">
                  <h1 className="text-base font-bold tracking-tight" data-testid="page-title">
                    Cross-Branch Payment
                  </h1>
                </div>
                <div className={`flex-1 min-w-0 rounded-xl border px-3 py-2 transition-colors ${selectedCustomer ? 'bg-[#1A4D2E]/5 border-[#1A4D2E]/30' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <Users size={14} className={selectedCustomer ? 'text-[#1A4D2E] shrink-0' : 'text-slate-400 shrink-0'} />
                    {selectedCustomer ? (
                      <p className="text-base font-bold text-slate-800 leading-tight break-words flex-1"
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        data-testid="payment-customer-display"
                        title={selectedCustomer.name}>
                        {selectedCustomer.name}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-400 italic flex-1" data-testid="payment-customer-display">
                        Select a customer from the left panel
                      </p>
                    )}
                    {selectedCustomer && (
                      <button onClick={clearCustomer} className="text-slate-400 hover:text-slate-600 shrink-0" title="Clear selection">
                        <X size={15} />
                      </button>
                    )}
                  </div>
                  {selectedCustomer && (
                    <div className="flex items-center gap-3 mt-1 ml-6 text-[11px] text-slate-500">
                      <span>{invoices.length} open invoice{invoices.length !== 1 ? 's' : ''}</span>
                      <span className="text-teal-600 font-medium">at {creditBranchName}</span>
                    </div>
                  )}
                </div>
                {selectedCustomer && (
                  <div className="text-right shrink-0" data-testid="customer-balance-display">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wide leading-tight">Total Amount Due</p>
                    <p className="text-2xl font-bold text-red-600 leading-tight">
                      {formatPHP(totalOpen)}
                    </p>
                  </div>
                )}
              </div>

              {/* Row 2: Payment Amount + Ref + Methods */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 transition-all ${
                  parseFloat(paymentAmtInput) > 0
                    ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-100'
                    : 'bg-white border-[#1A4D2E]/40 ring-2 ring-[#1A4D2E]/10'
                }`}>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
                    parseFloat(paymentAmtInput) > 0 ? 'bg-emerald-500 text-white' : 'bg-[#1A4D2E] text-white'
                  }`}>
                    <DollarSign size={16} />
                  </div>
                  <div className="flex flex-col">
                    <Label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide leading-none">Payment Amount</Label>
                    <CalcInput
                      placeholder="0.00"
                      value={paymentAmtInput}
                      className="h-7 w-36 text-xl font-bold border-0 px-0 py-0 shadow-none focus-visible:ring-0"
                      data-testid="receive-amount"
                      disabled={!selectedCustomer}
                      selectOnFocus
                      onChange={(v) => autoApply(v)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-[10px] text-slate-400 uppercase">Ref #</Label>
                  <Input value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="Check #, OR#..." className="h-9 w-32" data-testid="payment-ref" />
                </div>
                <Separator orientation="vertical" className="h-7 hidden sm:block" />
                <div className="flex gap-1 ml-auto" data-testid="payment-methods">
                  {METHODS.map(m => {
                    const Icon = m.icon;
                    const active = payMethod === m.value;
                    return (
                      <button key={m.value} onClick={() => setPayMethod(m.value)} data-testid={`pay-method-${m.value}`}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs transition-all ${
                          active ? 'bg-[#1A4D2E] text-white border-[#1A4D2E] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}>
                        <Icon size={13} />
                        <span className="text-[11px] font-medium">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ═══════════ MAIN CONTENT ═══════════ */}
            {!selectedCustomer ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Users size={48} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-slate-400 text-sm">Select a customer from the left or search to receive payment</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col gap-3 mt-3 overflow-hidden min-h-0 px-4">

                {/* ── Outstanding Transactions Table ── */}
                <Card className="border-slate-200 flex-1 min-h-0 flex flex-col">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-semibold">Outstanding Transactions</span>
                        {invoices.length > 0 && (
                          <button onClick={() => autoApply(totalOpen)} className="text-xs text-[#1A4D2E] hover:underline flex items-center gap-1 font-medium" data-testid="auto-apply-all-btn">
                            <Zap size={11} /> Auto-apply all
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span className="text-teal-600 font-semibold">AR at {creditBranchName}</span>
                      </div>
                    </div>
                  </div>

                  {loadingInvoices ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : invoices.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center"><Receipt size={36} className="mx-auto mb-2 opacity-20 text-slate-300" /><p className="text-sm text-slate-400">No open invoices at {creditBranchName}</p></div>
                    </div>
                  ) : (
                    <ScrollArea className="flex-1">
                      <table className="w-full text-sm" data-testid="invoices-table">
                        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                          <tr>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Number</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Type</th>
                            <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Orig. Amt</th>
                            <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Amt. Due</th>
                            <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase w-36">Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map(inv => {
                            const tc = getTypeConfig(inv.sale_type);
                            return (
                              <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`invoice-row-${inv.id}`}>
                                <td className="px-3 py-2.5 text-xs text-slate-500">{inv.order_date}</td>
                                <td className="px-3 py-2.5 text-xs text-slate-700">{inv.invoice_number}</td>
                                <td className="px-3 py-2.5">
                                  <Badge className={`text-[9px] ${tc.cls}`}>{tc.label}</Badge>
                                </td>
                                <td className="px-3 py-2.5 text-right text-xs">{formatPHP(inv.grand_total)}</td>
                                <td className="px-3 py-2.5 text-right font-semibold text-red-600">{formatPHP(inv.balance)}</td>
                                <td className="px-3 py-2.5 text-right">
                                  <CalcInput
                                    value={rowAmounts[inv.id] || ''}
                                    onChange={v => setRowAmounts(prev => ({ ...prev, [inv.id]: v }))}
                                    selectOnFocus
                                    placeholder="0.00"
                                    w-full h-8 text-right text-sm border border-slate-200 rounded px-2
                                    data-testid={`pay-row-${inv.id}`}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </ScrollArea>
                  )}
                </Card>

                {/* ── Footer: Photos + PIN + Account Summary + Actions ── */}
                {invoices.length > 0 && (
                  <div className="shrink-0 flex items-end gap-4 pb-3" data-testid="payment-footer">

                    {/* Photo uploads */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowNotebookUpload(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 transition"
                        data-testid="upload-notebook-btn"
                      >
                        <Camera size={13} />
                        Notebook {notebookPhotoCount > 0 && <Badge className="text-[9px] bg-green-100 text-green-700 ml-0.5">{notebookPhotoCount}</Badge>}
                      </button>
                      <button
                        onClick={() => setShowReceiptUpload(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 transition"
                        data-testid="upload-receipt-btn"
                      >
                        <Camera size={13} />
                        Receipt {receiptPhotoCount > 0 && <Badge className="text-[9px] bg-green-100 text-green-700 ml-0.5">{receiptPhotoCount}</Badge>}
                      </button>
                    </div>

                    {/* PIN + Memo */}
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2 py-1">
                        <Lock size={12} className="text-slate-400" />
                        <Input
                          type="password"
                          value={pin}
                          onChange={e => setPin(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && pin && handleSubmit()}
                          placeholder="PIN"
                          className="w-20 h-7 border-0 text-sm p-0 shadow-none focus-visible:ring-0"
                          data-testid="pin-input"
                        />
                      </div>
                    </div>

                    <div className="flex-1" />

                    {/* Account Summary */}
                    <div className="text-right text-xs space-y-0.5 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid="account-summary">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Account Summary</p>
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-slate-500">Outstanding Principal</span>
                        <span className="font-semibold">{formatPHP(principalOpen)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-amber-600">Accrued Interest Charges</span>
                        <span className="font-semibold text-amber-600">{formatPHP(interestOpen)}</span>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex items-center justify-between gap-6">
                        <span className="font-semibold text-slate-700">Total Amount Due</span>
                        <span className="font-bold text-red-600 text-base">{formatPHP(totalOpen)}</span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-1.5">
                      <Button
                        onClick={handleSubmit}
                        disabled={processing || totalApplied <= 0 || !pin}
                        className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-5 gap-1"
                        data-testid="submit-payment-btn"
                      >
                        {processing ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
                        Save & Apply
                      </Button>
                      <Button variant="outline" size="sm" onClick={clearCustomer} data-testid="clear-btn">Clear</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════ INTER-BRANCH ACCOUNT TAB ══════════ */}
      {pageTab === 'ledger' && (
        <div className="flex-1 overflow-auto px-4 py-4" data-testid="inter-branch-ledger-tab">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold" style={{ fontFamily: 'Manrope' }}>Inter-Branch Account</h2>
              <p className="text-xs text-slate-500 mt-0.5">Traceability ledger for all cross-branch collections — grouped by counterpart branch</p>
            </div>
            <div className="flex items-center gap-2">
              {ledger && (
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                  <Wallet size={12} className="text-slate-400" />
                  Inter-branch wallet balance: <span className="font-bold text-slate-700">{formatPHP(ledger.inter_branch_wallet_balance || 0)}</span>
                </div>
              )}
              <Button variant="outline" size="sm" className="h-8 gap-1" onClick={loadLedger} disabled={loadingLedger} data-testid="ledger-refresh-btn">
                <RefreshCw size={12} className={loadingLedger ? 'animate-spin' : ''} /> Refresh
              </Button>
            </div>
          </div>

          {loadingLedger ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
          ) : !ledger ? null : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

              {/* ── Section A: What I owe to others (Collecting branch perspective) ── */}
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="ledger-owe-section">
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingDown size={15} className="text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800" style={{ fontFamily: 'Manrope' }}>Cash Collected for Other Branches</span>
                    <span className="text-[10px] text-amber-600">(I hold their cash)</span>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-amber-600">Total</p>
                    <p className="text-base font-bold text-amber-700" data-testid="grand-owe-total">{formatPHP((ledger.grand_owe_unsettled || 0) + (ledger.grand_owe_settled || 0))}</p>
                  </div>
                </div>

                {ledger.owe_to_others.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-400">No inter-branch collections recorded yet</div>
                ) : (
                  <div>
                    {ledger.owe_to_others.map(group => (
                      <div key={group.branch_name} className="border-b border-slate-100 last:border-0">
                        {/* Group header */}
                        <button
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-50/40 transition-colors"
                          onClick={() => toggleOweGroup(group.branch_name)}
                          data-testid={`owe-group-${group.branch_name}`}>
                          <div className="flex items-center gap-2">
                            <ChevronRight size={13} className={`text-slate-400 transition-transform ${expandedOweGroups.has(group.branch_name) ? 'rotate-90' : ''}`} />
                            <span className="text-sm font-semibold text-slate-800">{group.branch_name}</span>
                            <span className="text-[10px] text-slate-400">{group.payments.length} transaction{group.payments.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="text-right">
                              <p className="text-[9px] text-amber-600 leading-tight">Unsettled</p>
                              <p className="text-sm font-bold text-amber-700">{formatPHP(group.total_unsettled)}</p>
                            </div>
                            {canSettle && group.total_unsettled > 0 && (
                              <span
                                role="button"
                                className="h-7 px-2.5 inline-flex items-center rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold"
                                onClick={(e) => { e.stopPropagation(); openSettleDialog(group); }}
                                data-testid={`settle-btn-${group.branch_name}`}>
                                Settle
                              </span>
                            )}
                          </div>
                        </button>

                        {/* Group rows */}
                        {expandedOweGroups.has(group.branch_name) && (
                          <div className="bg-slate-50/60 border-t border-slate-100">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-100">
                                  <th className="px-4 py-1.5 text-left text-[9px] font-semibold text-slate-400 uppercase">CBP #</th>
                                  <th className="px-4 py-1.5 text-left text-[9px] font-semibold text-slate-400 uppercase">Customer</th>
                                  <th className="px-4 py-1.5 text-right text-[9px] font-semibold text-slate-400 uppercase">Amount</th>
                                  <th className="px-4 py-1.5 text-center text-[9px] font-semibold text-slate-400 uppercase">Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.payments.map(p => (
                                  <tr key={p.id} className="border-b border-slate-100">
                                    <td className="px-4 py-2 font-medium text-slate-700">{p.payment_number}</td>
                                    <td className="px-4 py-2 text-slate-600 max-w-[120px] truncate">{p.customer_name}</td>
                                    <td className="px-4 py-2 text-right font-semibold text-slate-800">{formatPHP(p.amount)}</td>
                                    <td className="px-4 py-2 text-center text-slate-400">{p.finalized_at || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Grand total row */}
                    <div className="px-4 py-2.5 bg-amber-50/60 border-t border-amber-100 flex items-center justify-between">
                      <span className="text-xs font-semibold text-amber-800">Total Collected for Other Branches</span>
                      <div className="text-right">
                        <p className="text-sm font-bold text-amber-700">{formatPHP((ledger.grand_owe_unsettled || 0) + (ledger.grand_owe_settled || 0))}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Section B: What others collected for me (Credit branch perspective) ── */}
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="ledger-owed-section">
                <div className="px-4 py-3 bg-teal-50 border-b border-teal-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={15} className="text-teal-600" />
                    <span className="text-sm font-semibold text-teal-800" style={{ fontFamily: 'Manrope' }}>Collected by Other Branches for Me</span>
                    <span className="text-[10px] text-teal-600">(they hold my cash)</span>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-teal-600">Total</p>
                    <p className="text-base font-bold text-teal-700" data-testid="grand-owed-total">{formatPHP((ledger.grand_owed_unsettled || 0) + (ledger.grand_owed_settled || 0))}</p>
                  </div>
                </div>

                {ledger.owed_by_others.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-400">No collections received from other branches</div>
                ) : (
                  <div>
                    {ledger.owed_by_others.map(group => (
                      <div key={group.branch_name} className="border-b border-slate-100 last:border-0">
                        <button
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-teal-50/40 transition-colors"
                          onClick={() => toggleOwedGroup(group.branch_name)}
                          data-testid={`owed-group-${group.branch_name}`}>
                          <div className="flex items-center gap-2">
                            <ChevronRight size={13} className={`text-slate-400 transition-transform ${expandedOwedGroups.has(group.branch_name) ? 'rotate-90' : ''}`} />
                            <span className="text-sm font-semibold text-slate-800">{group.branch_name}</span>
                            <span className="text-[10px] text-slate-400">{group.payments.length} transaction{group.payments.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="text-right">
                              <p className="text-[9px] text-teal-600 leading-tight">Total</p>
                              <p className="text-sm font-bold text-teal-700">{formatPHP(group.total_unsettled + group.total_settled)}</p>
                            </div>
                          </div>
                        </button>

                        {expandedOwedGroups.has(group.branch_name) && (
                          <div className="bg-slate-50/60 border-t border-slate-100">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-100">
                                  <th className="px-4 py-1.5 text-left text-[9px] font-semibold text-slate-400 uppercase">CBP #</th>
                                  <th className="px-4 py-1.5 text-left text-[9px] font-semibold text-slate-400 uppercase">Customer</th>
                                  <th className="px-4 py-1.5 text-right text-[9px] font-semibold text-slate-400 uppercase">Amount</th>
                                  <th className="px-4 py-1.5 text-center text-[9px] font-semibold text-slate-400 uppercase">Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.payments.map(p => (
                                  <tr key={p.id} className="border-b border-slate-100">
                                    <td className="px-4 py-2 font-medium text-slate-700">{p.payment_number}</td>
                                    <td className="px-4 py-2 text-slate-600 max-w-[120px] truncate">{p.customer_name}</td>
                                    <td className="px-4 py-2 text-right font-semibold text-slate-800">{formatPHP(p.amount)}</td>
                                    <td className="px-4 py-2 text-center text-slate-400">{p.finalized_at || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="px-4 py-2.5 bg-teal-50/60 border-t border-teal-100 flex items-center justify-between">
                      <span className="text-xs font-semibold text-teal-800">Total Collected by Other Branches for Me</span>
                      <div className="text-right">
                        <p className="text-sm font-bold text-teal-700">{formatPHP((ledger.grand_owed_unsettled || 0) + (ledger.grand_owed_settled || 0))}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      )}

      {/* ── Bulk Settlement Dialog (BT Batch 2 P2) ── */}
      {settleDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="settle-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>
                  {settleResult ? `Settled — ${settleResult.st_number}` : `Settle with ${settleDialog.group.branch_name}`}
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {settleResult
                    ? 'Reference minted. Print the document and attach the bank receipt photo.'
                    : `All ${settleDialog.group.payments.filter(p => p.settlement_status !== 'settled').length} unsettled collection(s) — ${formatPHP(settleDialog.group.total_unsettled)}`}
                </p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setSettleDialog(null)} data-testid="settle-close">
                <X size={16} />
              </button>
            </div>

            {!settleResult ? (
              <div className="p-5 space-y-4">
                {settlePosition && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="settle-position-card">
                    <div>
                      <p className="text-slate-400 uppercase text-[9px] font-semibold">This branch owes (all)</p>
                      <p className="font-bold text-amber-700">{formatPHP(settlePosition.owes.total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase text-[9px] font-semibold">Owed to it</p>
                      <p className="font-bold text-teal-700">{formatPHP(settlePosition.owed.total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase text-[9px] font-semibold">This month net</p>
                      <p className={`font-bold ${settlePosition.month.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatPHP(settlePosition.month.net)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase text-[9px] font-semibold">Wallets</p>
                      <p className="font-medium text-slate-600">
                        {(settlePosition.wallets || []).map(w => `${w.name || w.type}: ${formatPHP(w.balance)}`).join(' · ') || '—'}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs font-semibold">How is this being settled?</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    <button
                      className={`rounded-lg border p-2.5 text-left ${settleMode === 'internal' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
                      onClick={() => setSettleMode('internal')} data-testid="settle-mode-internal">
                      <p className="text-xs font-bold">Internal</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Owner settles centrally. Bank ref &amp; photo optional.</p>
                    </button>
                    <button
                      className={`rounded-lg border p-2.5 text-left ${settleMode === 'documented' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
                      onClick={() => setSettleMode('documented')} data-testid="settle-mode-documented">
                      <p className="text-xs font-bold">Bank Transfer</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Bank transaction ref required; attach the receipt photo.</p>
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Bank transaction reference{settleMode === 'documented' ? ' *' : ' (optional)'}</Label>
                  <Input value={settleBankRef} onChange={e => setSettleBankRef(e.target.value)}
                    placeholder="e.g. INSTAPAY-2026082512345" className="h-9 mt-1" data-testid="settle-bank-ref" />
                </div>
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Input value={settleNotes} onChange={e => setSettleNotes(e.target.value)}
                    placeholder="e.g. weekly settlement" className="h-9 mt-1" data-testid="settle-notes" />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => setSettleDialog(null)}>Cancel</Button>
                  <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    disabled={settleBusy} onClick={submitSettle} data-testid="settle-submit">
                    {settleBusy ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
                    Settle {formatPHP(settleDialog.group.total_unsettled)}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400">
                  Every settlement mints an ST reference and goes to the Audit Center verify queue — internal mode only relaxes the evidence, never the paper trail.
                </p>
              </div>
            ) : (
              <div className="p-5 space-y-4" data-testid="settle-success">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                  <p className="text-[10px] uppercase text-emerald-600 font-semibold">Settlement Reference</p>
                  <p className="text-xl font-bold text-emerald-800 font-mono" data-testid="settle-st-number">{settleResult.st_number}</p>
                  <p className="text-[11px] text-emerald-700 mt-1">
                    {settleResult.payment_count} payment(s) · {formatPHP(settleResult.total_amount)} · {settleResult.mode === 'documented' ? 'Bank transfer' : 'Internal'}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="outline" onClick={printSettlement} data-testid="settle-print-btn">
                    <FileText size={13} className="mr-1" />Print Settlement Document
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSettlePhotoQR(true)} data-testid="settle-photo-btn">
                    <Camera size={13} className="mr-1" />Attach Bank Receipt Photo (QR)
                  </Button>
                  <Button size="sm" className="bg-slate-800 text-white" onClick={() => setSettleDialog(null)}>Done</Button>
                </div>
                <p className="text-[10px] text-slate-400">
                  Awaiting admin verification in Audit Center → Unverified.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Dialogs */}
      <UploadQRDialog
        open={showNotebookUpload}
        onClose={(count) => { setShowNotebookUpload(false); if (count > 0) setNotebookPhotoCount(prev => prev + count); }}
        recordType="cross_branch_payment_notebook"
        recordId={flowId}
      />
      <UploadQRDialog
        open={showReceiptUpload}
        onClose={(count) => { setShowReceiptUpload(false); if (count > 0) setReceiptPhotoCount(prev => prev + count); }}
        recordType="cross_branch_payment_receipt"
        recordId={flowId}
      />
      <UploadQRDialog
        open={settlePhotoQR}
        onClose={(count) => { setSettlePhotoQR(false); if (count > 0) toast.success(`${count} photo(s) attached to ${settleResult?.st_number || 'settlement'}.`); }}
        recordType="settlement"
        recordId={settleResult?.id}
      />
    </div>
  );
}
